import { useToast } from '@chakra-ui/react';
import { DailyEventObject } from '@daily-co/daily-js';
import { useDaily, useMeetingState, useRecording } from '@daily-co/daily-react';
import { datadogLogs } from '@datadog/browser-logs';
import { Toast } from '@web-app/components/Toast';
import { callLogContext } from '@web-app/utils/daily-client/call-log-context';
import { useCallback, useEffect, useRef } from 'react';

const TWENTY_MINUTES_IN_MILLISECONDS = 20 * 1000 * 60;
const RECORDING_CONFIRM_TIMEOUT_MS = 25000;
const RECORDING_ERROR_RETRY_MS = 1500;
const MAX_RECORDING_RETRIES = 3;

interface UseAutoRecordingArgs {
  roomId: string;
  participantCount: number;
  isProvider: boolean;
}

/**
 * Hook that automatically starts recording when provider + patient are present.
 *
 * Recording operations are fire-and-forget (startRecording returns void), so
 * success/failure is determined by Daily's event callbacks and a watchdog:
 * - onRecordingStarted: recording is confirmed active
 * - onRecordingError: recording failed, retried after a short backoff
 * - watchdog timer: retries silent non-starts with no Daily event
 */
export function useAutoRecording({
  roomId,
  participantCount,
  isProvider,
}: UseAutoRecordingArgs) {
  const daily = useDaily();
  const meetingState = useMeetingState();
  const toast = useToast({
    position: 'top-right',
    duration: null,
    isClosable: false,
  });

  const retryCountRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRecordingRef = useRef(false);
  const hasShownRecordingIssueToastRef = useRef(false);
  const attemptStartRecordingRef = useRef<() => void>(() => undefined);
  const autoStartRoomRef = useRef('');

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const scheduleTimer = useCallback(
    (callback: () => void, delayMs: number) => {
      clearTimer();
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        callback();
      }, delayMs);
    },
    [clearTimer],
  );

  const showRecordingNotWorkingToast = useCallback(() => {
    toast({
      render: ({ onClose }) => (
        <Toast
          title="Recording not working"
          text="Take notes as transcription isn't available. Rejoining the call may help."
          onClose={onClose}
        />
      ),
    });
  }, [toast]);

  const showRecordingIssueToast = useCallback(() => {
    if (hasShownRecordingIssueToastRef.current) {
      return;
    }

    hasShownRecordingIssueToastRef.current = true;
    toast({
      render: ({ onClose }) => (
        <Toast
          title="Recording issue"
          text="Having trouble starting the recording. Retrying automatically..."
          onClose={onClose}
        />
      ),
    });
  }, [toast]);

  const failAttempt = useCallback(
    (
      reason: 'recording-error' | 'watchdog-timeout',
      attrs: { errorMsg?: string } = {},
    ) => {
      clearTimer();

      if (isRecordingRef.current) {
        return;
      }

      retryCountRef.current += 1;
      const attempt = retryCountRef.current;
      const currentMeetingState = daily?.meetingState?.();
      const isTerminalAttempt = attempt >= MAX_RECORDING_RETRIES;

      if (reason === 'recording-error') {
        datadogLogs.logger.warn(
          'Recording error',
          callLogContext('auto-recording', {
            roomId,
            participantType: 'provider',
            errorMsg: attrs.errorMsg,
            attempt,
            maxRetries: MAX_RECORDING_RETRIES,
            meetingState: currentMeetingState,
          }),
        );

        if (!isTerminalAttempt) {
          showRecordingIssueToast();
        }
      } else if (reason === 'watchdog-timeout' && !isTerminalAttempt) {
        datadogLogs.logger.warn(
          'Recording start watchdog timed out, retrying',
          callLogContext('auto-recording', {
            roomId,
            participantType: 'provider',
            attempt,
            maxRetries: MAX_RECORDING_RETRIES,
            meetingState: currentMeetingState,
            timeoutMs: RECORDING_CONFIRM_TIMEOUT_MS,
          }),
        );
      }

      if (isTerminalAttempt) {
        datadogLogs.logger.error(
          'Recording failed after max retries',
          callLogContext('auto-recording', {
            roomId,
            participantType: 'provider',
            reason,
            errorMsg: attrs.errorMsg,
            attempt,
            maxRetries: MAX_RECORDING_RETRIES,
            meetingState: currentMeetingState,
          }),
        );

        showRecordingNotWorkingToast();
        return;
      }

      if (reason === 'recording-error') {
        scheduleTimer(
          () => attemptStartRecordingRef.current(),
          RECORDING_ERROR_RETRY_MS,
        );
        return;
      }

      attemptStartRecordingRef.current();
    },
    [
      clearTimer,
      daily,
      roomId,
      scheduleTimer,
      showRecordingIssueToast,
      showRecordingNotWorkingToast,
    ],
  );

  const { startRecording, isRecording } = useRecording({
    onRecordingStarted: () => {
      clearTimer();
      // Sync ref here, not only via the isRecording effect (one commit later):
      // a watchdog callback already queued this tick would otherwise see false
      // and fire a redundant startRecording.
      isRecordingRef.current = true;
      hasShownRecordingIssueToastRef.current = false;

      if (retryCountRef.current > 0) {
        toast({
          render: ({ onClose }) => (
            <Toast
              title="Recording active"
              text="Recording successfully resumed."
              onClose={onClose}
            />
          ),
        });
      }

      retryCountRef.current = 0;

      datadogLogs.logger.info(
        'Recording started',
        callLogContext('auto-recording', {
          roomId,
          participantType: 'provider',
          participantCount,
        }),
      );
    },

    onRecordingError: (ev: DailyEventObject<'recording-error'>) => {
      failAttempt('recording-error', { errorMsg: ev?.errorMsg });
    },
  });

  const attemptStartRecording = useCallback(() => {
    if (!(participantCount >= 2 && isProvider)) {
      clearTimer();
      return;
    }

    if (isRecordingRef.current) {
      clearTimer();
      return;
    }

    const currentMeetingState = daily?.meetingState?.();

    if (currentMeetingState !== 'joined-meeting') {
      datadogLogs.logger.info(
        'Recording start deferred for meeting state',
        callLogContext('auto-recording', {
          roomId,
          participantType: 'provider',
          participantCount,
          meetingState: currentMeetingState,
          attempt: retryCountRef.current,
          maxRetries: MAX_RECORDING_RETRIES,
        }),
      );

      scheduleTimer(
        () => attemptStartRecordingRef.current(),
        RECORDING_CONFIRM_TIMEOUT_MS,
      );
      return;
    }

    startRecording({
      minIdleTimeOut: TWENTY_MINUTES_IN_MILLISECONDS,
      layout: { preset: 'audio-only' },
    });

    scheduleTimer(
      () => failAttempt('watchdog-timeout'),
      RECORDING_CONFIRM_TIMEOUT_MS,
    );

    datadogLogs.logger.info(
      'Auto-start requested',
      callLogContext('auto-recording', {
        roomId,
        participantType: 'provider',
        participantCount,
        attempt: retryCountRef.current,
      }),
    );
  }, [
    clearTimer,
    daily,
    failAttempt,
    isProvider,
    participantCount,
    roomId,
    scheduleTimer,
    startRecording,
  ]);

  attemptStartRecordingRef.current = attemptStartRecording;

  useEffect(
    function syncRecordingRef() {
      isRecordingRef.current = isRecording;
    },
    [isRecording],
  );

  /**
   * Resets retryCountRef before each fresh auto-start for leave/rejoin and room
   * changes. Meeting-state-only changes should not reset the retry cycle.
   */
  useEffect(
    function autoStartRecordingWhenParticipantsJoin() {
      if (!(participantCount >= 2 && isProvider)) {
        clearTimer();
        retryCountRef.current = 0;
        hasShownRecordingIssueToastRef.current = false;
        autoStartRoomRef.current = '';
        return;
      }

      if (isRecording) {
        return;
      }

      // Reset the retry cycle only on a fresh session (new room or a
      // leave/rejoin, which clears the ref above), not on meeting-state changes.
      if (autoStartRoomRef.current !== roomId) {
        retryCountRef.current = 0;
        hasShownRecordingIssueToastRef.current = false;
        autoStartRoomRef.current = roomId;
      }

      if (retryCountRef.current >= MAX_RECORDING_RETRIES) {
        return;
      }

      attemptStartRecordingRef.current();
    },
    [
      roomId,
      participantCount,
      isRecording,
      isProvider,
      meetingState,
      clearTimer,
    ],
  );

  useEffect(
    function cleanupRecordingTimers() {
      return () => {
        clearTimer();
      };
    },
    [clearTimer],
  );
}
